import { apiClient } from '@/shared/api';

import type { GoogleSheetSettings, GoogleSheetSettingsRequest, ImportRun } from '../model/types';

const BASE = '/api/v1/catalog/import';

/** Download the .xlsx import template and trigger a browser save. */
export const downloadCatalogTemplate = async (): Promise<void> => {
  // Accept anything: the BFF serves the template as the .xlsx vendor content type
  // (application/vnd.openxmlformats-officedocument.spreadsheetml.sheet). Requesting a
  // narrower type (e.g. application/octet-stream) makes Spring content-negotiation 406.
  const { data, headers } = await apiClient.get<Blob>(`${BASE}/template`, {
    responseType: 'blob',
    headers: { Accept: '*/*' },
  });
  const type =
    (headers as Record<string, string>)['content-type'] ??
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  const blob = data instanceof Blob ? data : new Blob([data], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'catalog-template.xlsx';
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

/** Bulk-import a filled template. `dryRun` validates without writing. */
export const importCatalogFile = async (file: File, dryRun = false): Promise<ImportRun> => {
  const form = new FormData();
  form.append('file', file);
  form.append('dryRun', String(dryRun));
  const { data } = await apiClient.post<ImportRun>(`${BASE}/file`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
};

export const getGoogleSheetSettings = async (): Promise<GoogleSheetSettings> =>
  (await apiClient.get<GoogleSheetSettings>(`${BASE}/google-sheet/settings`)).data;

export const updateGoogleSheetSettings = async (
  body: GoogleSheetSettingsRequest,
): Promise<GoogleSheetSettings> =>
  (await apiClient.put<GoogleSheetSettings>(`${BASE}/google-sheet/settings`, body)).data;

export const importFromGoogleSheet = async (dryRun = false): Promise<ImportRun> =>
  (await apiClient.post<ImportRun>(`${BASE}/google-sheet`, null, { params: { dryRun } })).data;

export const getImportRun = async (runId: string): Promise<ImportRun> =>
  (await apiClient.get<ImportRun>(`${BASE}/runs/${runId}`)).data;
